/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    HTTPLocalResponseStatusCode,
    HTTPSwitchingActionType,
    IHTTPLocalFile,
    IHTTPSwitchingAction,
} from 'generated-types';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';

import { HTTPSwitchingAction } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { SchemaService } from 'ajs/modules/core/services';

type THTTPSwitchingAction = Omit<IHTTPSwitchingAction, 'file'>;

interface IHTTPSwitchingActionConfig extends THTTPSwitchingAction {
    file?: MessageItem<IHTTPLocalFile>;
}

/**
 * Using values from HTTPPolicyVar enum to implement checks on component template.
 */
export const HTTPSwitchingVarHash = {
    OTHER_VALUE: 'OTHER_VALUE',
};

/**
 * @description HTTPSwitchingAction ConfigItem.
 * @author Abhinesh Gour, Umesh Chovatiya
 */
export class HTTPSwitchingActionConfigItem extends
    withFullModalMixin(MessageItem)<IHTTPSwitchingActionConfig> {
    /**
     * Dependencies that are required.
     */
    public static ajsDependencies = [
        'l10nService',
        'schemaService',
    ];

    /**
     * Schema Service Instance
     */
    private readonly schemaService: SchemaService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPSwitchingAction,
            ...args,
        };

        super(extendedArgs);

        this.schemaService = this.getAjsDependency_('schemaService');
    }

    /**
     * Getter for protocol string from HTTPProtocol Enum.
     */
    public get actionTypeName(): string {
        return this.config.action ?
            this.schemaService.getEnumValueLabel('HTTPSwitchingActionType',
                this.config.action) : '';
    }

    /**
     * Getter for Redirect Status Code string from HTTPRedirectStatusCode Enum.
     */
    public get redirectStatusCode(): string | number {
        return this.config.other_status_code ? this.config.other_status_code :
            this.schemaService.getEnumValueLabel('HTTPLocalResponseStatusCode',
                this.config.status_code);
    }

    /**
     * Create File config.
     */
    public createFile(): void {
        this.safeSetNewChildByField('file');
    }

    /**
     * Delete status code if OtherStatusCode is exists readonly component.
     */
    public clearOtherStatusCode(): void {
        if (this.config.status_code !== HTTPSwitchingVarHash.OTHER_VALUE) {
            delete this.config?.other_status_code;
        }
    }

    /**
     * Extracts value from the config and prepares a string
     * to show action details in the grid expander.
     */
    public actionReadonlyDetails(): string {
        const { action } = this.config;

        const { actionTypeName, redirectStatusCode } = this;

        if (action === HTTPSwitchingActionType.HTTP_SWITCHING_SELECT_POOL) {
            const poolFile = this.config.pool_ref?.split('#')[1];

            return `${actionTypeName}: ${poolFile}`;
        } else if (action === HTTPSwitchingActionType.HTTP_SWITCHING_SELECT_POOLGROUP) {
            const poolGroupFile = this.config.pool_group_ref?.split('#')[1];

            return `${actionTypeName}: ${poolGroupFile}`;
        }

        return `${actionTypeName}: ${redirectStatusCode}`;
    }

    /**
     * @override
     */
    protected modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        if (this.config.other_status_code) {
            this.config.status_code =
                HTTPSwitchingVarHash.OTHER_VALUE as HTTPLocalResponseStatusCode;
        }
    }

    /**
     * @override
     * Remove the status_code if Other status code is selected.
     */
    protected modifyConfigDataBeforeSave(): void {
        super.modifyConfigDataBeforeSave();

        if (this.config.other_status_code) {
            delete this.config?.status_code;
        }
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPSwitchingActionConfig> {
        const defaultValues = this.getAjsDependency_('defaultValues');
        const defaultConfig = defaultValues.getDefaultItemConfigByType(this.objectType) || {};

        return {
            ...defaultConfig,
            action: HTTPSwitchingActionType.HTTP_SWITCHING_SELECT_POOL,
        };
    }
}

